import React, { useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useSettings } from '../context/SettingsContext';

const ContactContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
`;

const Title = styled.h1`
  color: var(--primary-blue);
  margin-bottom: 2rem;
  text-align: center;
`;

const ContactForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  background: white;
  padding: 2rem;
  border-radius: var(--border-radius);
  box-shadow: var(--shadow);
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: var(--gray-dark);
`;

const Input = styled.input`
  padding: 0.8rem;
  border: 1px solid var(--gray-light);
  border-radius: var(--border-radius);
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: var(--primary-blue);
  }
`;

const TextArea = styled.textarea`
  padding: 0.8rem;
  border: 1px solid var(--gray-light);
  border-radius: var(--border-radius);
  font-size: 1rem;
  min-height: 150px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: var(--primary-blue);
  }
`;

const SubmitButton = styled.button`
  background-color: var(--primary-blue);
  color: white;
  border: none;
  padding: 1rem;
  font-size: 1rem;
  font-weight: 500;
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: var(--transition);
  
  &:hover {
    background-color: var(--primary-blue-dark);
  }
`;

const ContactInfo = styled.div`
  margin-top: 3rem;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
`;

const InfoCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: var(--border-radius);
  box-shadow: var(--shadow);
  text-align: center;
`;

const InfoTitle = styled.h3`
  color: var(--primary-blue);
  margin-bottom: 1rem;
`;

// Formatea para mostrar sin +51 y con espacios cada 3 dígitos
const formatPhoneDisplay = (raw?: string) => {
  if (!raw) return 'No configurado';
  const digits = raw.replace(/[^\d]/g, '');
  // Si empieza con 51 (Perú), quitarlo para mostrar número local
  const local = digits.startsWith('51') && digits.length > 9 ? digits.slice(2) : digits;
  // Agrupar en bloques de 3
  return local.replace(/(\d{3})(?=\d)/g, '$1 ').trim();
};

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const { settings } = useSettings();
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const rawNumber = settings.whatsapp_number || '';
      const phone = rawNumber.replace(/[^\d]/g, '');
      if (!phone) {
        toast.error('No hay un número de WhatsApp configurado.');
        return;
      }

      const text =
        `Hola, soy ${formData.name}.\n` +
        `Asunto: ${formData.subject}\n` +
        `Email: ${formData.email}\n\n` +
        `Mensaje:\n${formData.message}`;

      const url = `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;
      window.open(url, '_blank');

      toast.success('Abriendo WhatsApp...');
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error(error);
      toast.error('No se pudo abrir WhatsApp. Inténtalo de nuevo más tarde.');
    }
  };
  
  return (
    <ContactContainer>
      <Title>Contáctanos</Title>
      
      <ContactForm onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="name">Nombre</Label>
          <Input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
          />
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="email">Correo Electrónico</Label>
          <Input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="subject">Asunto</Label>
          <Input
            type="text"
            id="subject"
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            required
          />
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="message">Mensaje</Label>
          <TextArea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            required
          />
        </FormGroup>
        
        <SubmitButton type="submit">Enviar Mensaje</SubmitButton>
      </ContactForm>
      
      <ContactInfo>
        <InfoCard>
          <InfoTitle>Dirección</InfoTitle>
          <p>Av. Principal 123, Ciudad</p>
        </InfoCard>
        
        <InfoCard>
          <InfoTitle>Teléfono</InfoTitle>
          <p>{formatPhoneDisplay(settings.whatsapp_number)}</p>
        </InfoCard>
        
        <InfoCard>
          <InfoTitle>Email</InfoTitle>
          <p>info@grupovargas.com</p>
        </InfoCard>
      </ContactInfo>
    </ContactContainer>
  );
};

export default Contact;